/**
 * Questionnaire answers for the demo pool.
 *
 * Without these, the demo shows only the legacy four-axis score and incomplete
 * two-stage model never runs — gates and affinity both require BOTH sides to
 * have answered. These give the prototype something real to demonstrate:
 * varied faith intensity, a genuine gate failure, and axes that differ enough
 * that the weighting is visible rather than everyone landing on 90-something.
 *
 * Demo data only. Nothing here ships to production.
 */

import type { ProfileAnswers, FaithPercent } from "../domain/answers";
import { emptyProfileAnswers } from "../domain/answers";

export interface DemoAnswerSpec {
  faith: FaithPercent;
  /** Minimum faith they require in a partner. */
  requires: FaithPercent;
  commandments?: boolean;
  gospel?: boolean;
  church?: boolean;
  honesty?: number;
  peacemaker?: boolean;
  homebuilder?: boolean;
  humorous?: boolean;
  caring?: boolean;
  manners?: boolean;
  peacefulHome?: boolean;
  /** Home & self-sufficiency: herbs, growing, forest, prepping, farming, canning, survival. */
  home?: boolean[];
  children?: ProfileAnswers["children"];
  tattoos?: boolean;
  acceptsTattoos?: boolean;
  acceptsPastSubstances?: boolean;
  pastMarijuana?: boolean;
  ready?: boolean;
  own?: string[];
  accepts?: string[];
  interests?: string[];
}

const HOME_KEYS = [
  "herbKnowledge",
  "plantsOwnFruitVeg",
  "interestedInFruitForest",
  "preppingForCalamities",
  "communityFarming",
  "canningAndPreserving",
  "survivalSkills",
] as const;

export function demoAnswers(spec: DemoAnswerSpec): ProfileAnswers {
  const a = emptyProfileAnswers();

  a.faith.servingImportance = spec.faith;
  a.faith.keepsCommandments = spec.commandments ?? true;
  a.faith.spreadsGospel = spec.gospel ?? false;
  a.faith.grewUpInChurch = spec.church ?? true;
  a.faith.hasIdols = false;
  a.requiredPartnerFaith = spec.requires;

  a.character.honestyRating = spec.honesty ?? 8;
  a.character.peacemaker = spec.peacemaker ?? true;
  a.character.homebuilder = spec.homebuilder ?? true;
  a.character.humorous = spec.humorous ?? true;
  a.character.caringAffectionate = spec.caring ?? true;
  a.character.goodManners = spec.manners ?? true;
  a.character.wantsPeacefulHome = spec.peacefulHome ?? true;

  const home = spec.home ?? [false, false, false, false, false, false, false];
  HOME_KEYS.forEach((k, i) => {
    a.home[k] = home[i] ?? false;
  });

  a.children = spec.children ?? "wants";
  a.appearance.hasTattoos = spec.tattoos ?? false;
  a.appearance.acceptsPartnerTattoos = spec.acceptsTattoos ?? true;
  a.acceptsPastSubstanceUse = spec.acceptsPastSubstances ?? true;
  a.history.usedMarijuana = spec.pastMarijuana ?? false;
  a.history.readyForLastingRelationship = spec.ready ?? true;

  a.ancestry.own = spec.own ?? [];
  a.ancestry.accepted = spec.accepts ?? ["any"];
  a.interests = spec.interests ?? [];

  return a;
}

/** The guest "you" profile — a middle-of-the-road member so the deck varies. */
export const GUEST_ANSWERS: ProfileAnswers = demoAnswers({
  faith: 80,
  requires: 50,
  gospel: true,
  honesty: 9,
  home: [true, true, false, false, false, true, false],
  own: ["german"],
  accepts: ["any"],
  interests: ["reading", "hiking", "music", "cooking"],
});

/**
 * Per-candidate answers, keyed by demo profile id.
 *
 * All sixteen pass the guest (faith 80, requires 50) so a man sees eight
 * women and a woman sees eight men — four this week, four after the one
 * 10 000-credit shuffle. Axes still differ so ranking is visible.
 */
export const DEMO_ANSWERS: Record<string, ProfileAnswers> = {
  cand_f_1: demoAnswers({
    faith: 90, requires: 70, gospel: true, honesty: 9,
    home: [true, true, true, false, false, true, false],
    own: ["german"], interests: ["hiking", "music", "cooking", "reading"],
  }),
  cand_f_2: demoAnswers({
    faith: 70, requires: 50, honesty: 8, humorous: false,
    home: [false, true, false, false, false, true, false],
    own: ["english"], interests: ["reading", "art", "volunteering"],
  }),
  cand_f_3: demoAnswers({
    faith: 60, requires: 40, church: true, commandments: true, honesty: 7,
    home: [false, false, false, false, false, false, false],
    own: ["spanish"], interests: ["art", "running", "music"],
  }),
  cand_f_4: demoAnswers({
    faith: 80, requires: 60, gospel: true, honesty: 9,
    home: [true, false, false, false, false, false, false],
    own: ["irish"], interests: ["cooking", "cycling", "reading"],
  }),
  cand_f_5: demoAnswers({
    faith: 60, requires: 50, honesty: 7, tattoos: true,
    home: [false, false, true, true, false, false, true],
    own: ["dutch"], interests: ["climbing", "games", "coffee"],
  }),
  cand_m_1: demoAnswers({
    faith: 90, requires: 70, gospel: true, honesty: 9,
    home: [true, true, true, true, false, true, true],
    own: ["german"], interests: ["woodworking", "fishing", "hiking"],
  }),
  cand_m_2: demoAnswers({
    faith: 90, requires: 50, gospel: true, honesty: 9,
    home: [false, false, false, false, false, false, false],
    own: ["english"], interests: ["reading", "running", "cooking"],
  }),
  cand_m_3: demoAnswers({
    faith: 60, requires: 40, honesty: 7, pastMarijuana: true,
    home: [false, false, false, true, false, false, true],
    own: ["irish"], interests: ["basketball", "cooking", "volunteering"],
  }),
  cand_m_4: demoAnswers({
    faith: 80, requires: 50, honesty: 8,
    home: [true, true, true, true, true, true, true],
    own: ["dutch"], interests: ["hiking", "music", "cooking"],
  }),
  cand_m_5: demoAnswers({
    faith: 70, requires: 50, honesty: 9, homebuilder: false,
    home: [false, false, false, false, false, false, false],
    own: ["scottish"], interests: ["games", "tennis", "reading"],
  }),
  cand_f_6: demoAnswers({
    faith: 90, requires: 60, gospel: true, honesty: 8,
    home: [true, true, false, false, true, true, false],
    own: ["english"], interests: ["music", "cooking", "volunteering"],
  }),
  cand_f_7: demoAnswers({
    faith: 70, requires: 50, honesty: 8, humorous: true,
    home: [false, true, false, false, false, true, false],
    own: ["irish"], interests: ["reading", "hiking", "art"],
  }),
  cand_f_8: demoAnswers({
    faith: 60, requires: 50, honesty: 8,
    home: [true, false, true, false, false, false, false],
    own: ["german"], interests: ["gardening", "music", "cooking"],
  }),
  cand_m_6: demoAnswers({
    faith: 90, requires: 60, gospel: true, honesty: 9,
    home: [true, true, false, false, true, false, true],
    own: ["english"], interests: ["hiking", "music", "mentoring"],
  }),
  cand_m_7: demoAnswers({
    faith: 70, requires: 50, honesty: 8,
    home: [false, false, false, false, false, true, false],
    own: ["scottish"], interests: ["reading", "tennis", "cooking"],
  }),
  cand_m_8: demoAnswers({
    faith: 70, requires: 50, honesty: 8, homebuilder: true,
    home: [true, true, true, false, false, true, false],
    own: ["irish"], interests: ["fishing", "hiking", "woodworking"],
  }),
};
